/**
 * The pluggable quoting interface.
 *
 * LiberaDex does not run its own pools. Every swap is quoted across external
 * liquidity sources and the best result wins. Each source lives behind this one
 * interface so adding another is a new file plus a `registerProvider` call - never a
 * change to the swap UI or to the other providers.
 *
 * Nothing here imports React, ethers, or any provider SDK, so the whole routing layer
 * is unit-testable without a network or a wallet.
 */

/** Sentinel used by most aggregators to mean "the chain's native coin", i.e. BNB. */
export const NATIVE_TOKEN_SENTINEL = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'

export interface TokenRef {
  /** Checksummed contract address, or NATIVE_TOKEN_SENTINEL for the native coin. */
  address: string
  decimals: number
  symbol: string
}

export interface QuoteRequest {
  chainId: number
  sellToken: TokenRef
  buyToken: TokenRef
  /** Amount to sell, in the token's smallest unit (wei-equivalent), as a decimal string. */
  sellAmount: string
  /** Slippage tolerance in basis points. 50 = 0.5%. */
  slippageBps: number
  /** Connected wallet. Absent for indicative "what would I get" quotes. */
  taker?: string
  /** Where output tokens land. Defaults to the taker. */
  recipient?: string
}

/** One leg of a split route, for the "X% via PancakeSwap V3" breakdown. */
export interface RouteHop {
  /** Liquidity source name as reported by the aggregator, e.g. "PancakeSwap V3". */
  source: string
  /** Share of the trade routed through this source, in basis points of the total. */
  portionBps: number
}

export interface AppliedFee {
  bps: number
  recipient: string
  /** Fee taken, in the smallest unit of whichever token the source charges in. */
  amount?: string
  token?: TokenRef
}

/** A transaction ready to sign. Absent on indicative quotes (no `taker` supplied). */
export interface QuoteTransaction {
  to: string
  data: string
  /** Native value to attach, in wei, as a decimal string. */
  value: string
  gas?: string
}

export interface AggregatorQuote {
  /** Id of the provider that produced this quote. */
  providerId: string
  providerLabel: string
  sellAmount: string
  /** Expected output, in the buy token's smallest unit, net of the provider's fees. */
  buyAmount: string
  /** Worst acceptable output after slippage. What the user is actually guaranteed. */
  minBuyAmount: string
  estimatedGas?: string
  /**
   * Contract that must be approved to spend the sell token.
   *
   * This is per-quote, not a global constant: each aggregator settles through its own
   * router, and some rotate them. Approving a hardcoded address is how forks end up
   * sending allowances to a contract that cannot execute the trade.
   */
  allowanceTarget?: string
  route: RouteHop[]
  /** Negative means the user gets a worse rate than spot. 100 = 1%. */
  priceImpactBps?: number
  fee?: AppliedFee
  transaction?: QuoteTransaction
  /** Epoch ms after which this quote should be re-fetched before signing. */
  expiresAt?: number
  /** Untouched provider payload, for debugging and for fields we do not model. */
  raw?: unknown
}

export interface QuoteProvider {
  /** Stable machine id. Used in config, telemetry and the `raw` payload map. */
  id: string
  /** Name shown to users in the route breakdown. */
  label: string
  supportsChain(chainId: number): boolean
  /**
   * False when the provider needs credentials it has not been given. Unconfigured
   * providers are skipped rather than allowed to fail on every quote.
   */
  isConfigured(): boolean
  /** Rejects on failure; the registry isolates that so one bad source cannot blank the UI. */
  fetchQuote(request: QuoteRequest, signal: AbortSignal): Promise<AggregatorQuote>
}

export interface ProviderFailure {
  providerId: string
  providerLabel: string
  message: string
}

export interface AggregatedQuoteResult {
  /** Highest net output across every source that answered. Null if none did. */
  best: AggregatedQuoteResultBest | null
  /** Every successful quote, best first - powers a "compare sources" view. */
  quotes: AggregatorQuote[]
  /** Sources that errored, timed out, or had no route. Surfaced, never swallowed. */
  failures: ProviderFailure[]
}

export interface AggregatedQuoteResultBest extends AggregatorQuote {
  /**
   * How much better this quote is than the next best, in basis points.
   * Undefined when only one source answered. This is the number that evidences
   * the product's actual value: routing quality.
   */
  improvementBps?: number
}
